import React from "react";
import styled from "styled-components";
import { Lightbulb, AlertCircle } from "lucide-react";
import { Card, FlexContainer } from "../../styles/GlobalStyles";

const ProblemContainer = styled(Card)`
  border-left: 4px solid ${({ theme }) => theme.colors.warning};
`;

const SectionHeader = styled(FlexContainer)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.warning}15;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TagContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.warning}20;
  color: ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SkillItem = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 3px solid ${({ theme }) => theme.colors.info};
`;

const SkillTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SkillDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`;

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.warning};
`;

interface Skill {
  title: string;
  description: string;
}

interface ProblemResolutionCardProps {
  title?: string;
  description?: string;
  skills?: Skill[];
}

const defaultSkills: Skill[] = [
  {
    title: "Capacidade de compreender problemas",
    description:
      "Habilidade de analisar e identificar a raiz dos problemas de forma estruturada.",
  },
  {
    title: "Foco na solução",
    description:
      "Orientação para encontrar soluções práticas e eficazes para os desafios apresentados.",
  },
  {
    title: "Buscar alternativas",
    description:
      "Capacidade de explorar diferentes abordagens e alternativas para resolver questões complexas.",
  },
  {
    title: "Implementar soluções na prática",
    description:
      "Habilidade de colocar em ação as soluções identificadas de forma efetiva.",
  },
];

export const ProblemResolutionCard: React.FC<ProblemResolutionCardProps> = ({
  title = "Resolução de problemas",
  description = "Capacidade de compreender problemas em sua totalidade, focando na solução, buscando alternativas, colocando soluções em prática.",
  skills = defaultSkills,
}) => {
  return (
    <ProblemContainer>
      <SectionHeader gap="16px">
        <IconContainer>
          <AlertCircle size={24} />
        </IconContainer>
        <div style={{ flex: 1 }}>
          <SectionTitle>{title}</SectionTitle>
        </div>
        <TagContainer>
          <Lightbulb size={12} />
          Resposta é comentários obrigatórios
        </TagContainer>
      </SectionHeader>

      <Description>{description}</Description>

      <SkillsContainer>
        {skills.map((skill, index) => (
          <SkillItem key={index}>
            <SkillTitle>{skill.title}</SkillTitle>
            <SkillDescription>{skill.description}</SkillDescription>
          </SkillItem>
        ))}
      </SkillsContainer>
    </ProblemContainer>
  );
};
